import { RefreshCw, ScanLine, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createIntentKey,
  useOpeningImpactAuditQuery,
  useProjectCardsQuery,
  useProjectQuery,
  useRequestOpeningImpactAuditMutation,
  type OpeningImpactAudit,
  type TrackCard,
  type TrackCardQuery,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Banner,
  Button,
  FieldLabel,
  Input,
  Select,
  StateSurface,
} from '../../design-system/index.js';
import { PageHeader, SegmentedControl } from '../shared/page.js';
import { TrackCollection } from '../tracks/track-collection.js';
import { trackTitle } from '../tracks/track-presentation.js';
import { DurationRangeFilter } from './duration-range-filter.js';
import {
  ONSET_AUDIT_DURATION_CEILING_SECONDS,
  durationIsInRange,
  type DurationRange,
  type OnsetAuditFilter,
} from './onset-audit-model.js';

export function OpeningImpactAuditPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { connection, library } = useWorkspace();
  const projects = useProjectCardsQuery({
    connection_id: connection.id,
    library_id: library.id,
    limit: 100,
  });
  const project = useProjectQuery(projectId);
  const auditQuery = useOpeningImpactAuditQuery(projectId, connection.id, library.id);
  const requestAudit = useRequestOpeningImpactAuditMutation();
  const requestKey = useRef<string | null>(null);
  const [filter, setFilter] = useState<OnsetAuditFilter>('all');
  const [query, setQuery] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [durationRange, setDurationRange] = useState<DurationRange>({
    minimum: 0,
    maximum: ONSET_AUDIT_DURATION_CEILING_SECONDS,
  });

  useEffect(() => {
    setFilter('all');
    setQuery('');
    setSelectionMode(false);
    setDurationRange({ minimum: 0, maximum: ONSET_AUDIT_DURATION_CEILING_SECONDS });
    requestKey.current = null;
    requestAudit.reset();
  }, [projectId, requestAudit.reset]);

  const audit = auditQuery.data?.audit ?? null;
  const projectOptions = useMemo(
    () =>
      [...(projects.data?.items ?? [])]
        .sort((left, right) => left.title.localeCompare(right.title))
        .map((item) => ({
          value: item.id,
          label: `${item.title} · ${item.song_count} ${item.song_count === 1 ? 'song' : 'songs'}`,
        })),
    [projects.data?.items],
  );

  const runAudit = async () => {
    if (!projectId || requestAudit.isPending) return;
    requestKey.current ??= createIntentKey();
    try {
      await requestAudit.mutateAsync({
        projectId,
        connectionId: connection.id,
        libraryId: library.id,
        idempotencyKey: requestKey.current,
      });
      requestKey.current = null;
    } catch {
      // Mutation state renders the error and the same key is retained for an exact retry.
    }
  };

  const title = projectId
    ? (project.data?.title ?? 'Opening-impact audit')
    : 'Opening-impact audit';
  const active = audit?.status === 'queued' || audit?.status === 'running';
  const workspaceUnavailable =
    project.isLoading || project.isError || auditQuery.isLoading || auditQuery.isError;

  return (
    <div>
      <PageHeader
        eyebrow="Audio review workspace"
        title={title}
        description="Run the same analysis on any project and use its result as a loose review filter. The standard project page remains unchanged."
        action={
          projectId && audit ? (
            <Button
              disabled={active || requestAudit.isPending || workspaceUnavailable}
              onClick={() => void runAudit()}
            >
              {active || requestAudit.isPending ? (
                <RefreshCw aria-hidden="true" className="animate-spin" />
              ) : (
                <ScanLine aria-hidden="true" />
              )}
              {active || requestAudit.isPending ? 'Analyzing…' : 'Run again'}
            </Button>
          ) : undefined
        }
      />

      <section className="mb-5 rounded-md border border-border bg-bg-2 p-4">
        <div className="grid gap-2">
          <FieldLabel htmlFor="opening-impact-project">Project</FieldLabel>
          <Select
            disabled={projects.isLoading || projects.isError}
            id="opening-impact-project"
            onValueChange={(value) => navigate(`/audits/opening-impact/${value}`)}
            options={projectOptions}
            placeholder={projects.isLoading ? 'Loading projects…' : 'Choose a project'}
            value={projectId ?? ''}
          />
        </div>
      </section>

      {projects.isError ? (
        <StateSurface
          action={
            <Button onClick={() => void projects.refetch()} variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="Projects unavailable"
        />
      ) : !projectId ? (
        <StateSurface title="Choose a project">
          Select a project to run or review its opening-impact analysis.
        </StateSurface>
      ) : project.isLoading || auditQuery.isLoading ? (
        <StateSurface kind="loading" title="Loading audit workspace" />
      ) : project.isError || auditQuery.isError ? (
        <StateSurface
          action={
            <Button
              onClick={() => {
                void project.refetch();
                void auditQuery.refetch();
              }}
              variant="secondary"
            >
              Try again
            </Button>
          }
          kind="error"
          title="Audit workspace unavailable"
        />
      ) : !audit ? (
        <StateSurface
          action={
            <Button disabled={requestAudit.isPending} onClick={() => void runAudit()}>
              <ScanLine aria-hidden="true" />
              {requestAudit.isPending ? 'Starting…' : 'Run analysis'}
            </Button>
          }
          title="No analysis has been run"
        >
          This creates a review result for the songs currently in{' '}
          {project.data?.title ?? 'this project'}.
        </StateSurface>
      ) : (
        <AuditResults
          audit={audit}
          connectionId={connection.id}
          durationRange={durationRange}
          filter={filter}
          libraryId={library.id}
          onDurationRangeChange={setDurationRange}
          onFilterChange={setFilter}
          onQueryChange={setQuery}
          onSelectionModeChange={setSelectionMode}
          projectTitle={project.data?.title ?? 'Project'}
          query={query}
          selectionMode={selectionMode}
        />
      )}

      {requestAudit.error ? (
        <Banner className="mt-4" tone="danger" title="Analysis could not be started">
          Nothing was changed. Try the request again.
        </Banner>
      ) : null}
    </div>
  );
}

function AuditResults({
  audit,
  connectionId,
  durationRange,
  filter,
  libraryId,
  onDurationRangeChange,
  onFilterChange,
  onQueryChange,
  onSelectionModeChange,
  projectTitle,
  query,
  selectionMode,
}: {
  audit: OpeningImpactAudit;
  connectionId: string;
  durationRange: DurationRange;
  filter: OnsetAuditFilter;
  libraryId: string;
  onDurationRangeChange: (range: DurationRange) => void;
  onFilterChange: (filter: OnsetAuditFilter) => void;
  onQueryChange: (query: string) => void;
  onSelectionModeChange: (active: boolean) => void;
  projectTitle: string;
  query: string;
  selectionMode: boolean;
}) {
  const resultByTrackId = useMemo(
    () => new Map(audit.results.map((result) => [result.track_id, result])),
    [audit.results],
  );
  const rankByTrackId = useMemo(() => {
    const statusOrder = { succeeded: 0, pending: 1, failed: 2 } as const;
    const ranked = [...audit.results].sort((left, right) => {
      if (left.status !== right.status) return statusOrder[left.status] - statusOrder[right.status];
      return (right.score ?? -1) - (left.score ?? -1);
    });
    return new Map(ranked.map((result, index) => [result.track_id, index]));
  }, [audit.results]);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const durationFilterActive =
    durationRange.minimum !== 0 || durationRange.maximum !== ONSET_AUDIT_DURATION_CEILING_SECONDS;
  const transformTracks = useCallback(
    (tracks: readonly TrackCard[]) =>
      tracks
        .filter((track) => {
          const result = resultByTrackId.get(track.id);
          if (!result) return false;
          if (normalizedQuery && !trackTitle(track).toLocaleLowerCase().includes(normalizedQuery)) {
            return false;
          }
          if (durationFilterActive && !durationIsInRange(track.duration_seconds, durationRange)) {
            return false;
          }
          if (filter === 'violates')
            return result.status === 'succeeded' && result.violates === true;
          if (filter === 'does-not-violate') {
            return result.status === 'succeeded' && result.violates === false;
          }
          return true;
        })
        .sort((left, right) => rankByTrackId.get(left.id)! - rankByTrackId.get(right.id)!),
    [durationFilterActive, durationRange, filter, normalizedQuery, rankByTrackId, resultByTrackId],
  );
  const trackQuery = useMemo<TrackCardQuery>(
    () => ({
      connection_id: connectionId,
      library_id: libraryId,
      composer_project_id: audit.composer_project_id,
      favorite: 'any',
      limit: 100,
    }),
    [audit.composer_project_id, connectionId, libraryId],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'project' as const,
      id: `opening-impact:${audit.id}:${filter}:${query}:${durationRange.minimum}:${durationRange.maximum}`,
      label: `${projectTitle} opening-impact review`,
    }),
    [audit.id, durationRange.maximum, durationRange.minimum, filter, projectTitle, query],
  );
  const selectionScopeKey = `${audit.id}:${filter}:${query}:${durationRange.minimum}:${durationRange.maximum}`;
  const detectionOptions = [
    { label: `All ${audit.counts.total}`, value: 'all' },
    { label: `Violates ${audit.counts.violates}`, value: 'violates' },
    {
      label: `Does not violate ${audit.counts.does_not_violate}`,
      value: 'does-not-violate',
    },
  ] as const;

  return (
    <>
      {audit.status === 'queued' || audit.status === 'running' ? (
        <Banner className="mb-4" title="Analysis in progress">
          {audit.counts.analyzed + audit.counts.failed} of {audit.counts.total} songs processed.
        </Banner>
      ) : audit.status === 'failed' ? (
        <Banner className="mb-4" tone="danger" title="Analysis failed">
          {audit.failure?.message ?? 'The analysis could not be completed.'}{' '}
          {audit.counts.failed > 0
            ? `${audit.counts.failed} ${audit.counts.failed === 1 ? 'song failed' : 'songs failed'} before analysis.`
            : 'Run the analysis again.'}
        </Banner>
      ) : audit.counts.failed > 0 ? (
        <Banner className="mb-4" tone="warning" title="Some songs could not be analyzed">
          {audit.counts.failed} {audit.counts.failed === 1 ? 'song is' : 'songs are'} still included
          in All, but not in either result filter.
        </Banner>
      ) : null}

      <section className="mb-5 rounded-md border border-border bg-bg-2 p-4">
        <div className="grid gap-5">
          <SegmentedControl
            label="Script result"
            onChange={onFilterChange}
            options={detectionOptions}
            value={filter}
          />
          <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <label className="block">
              <span className="mb-2 block font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
                Search title
              </span>
              <Input
                maxLength={256}
                onChange={(event) => onQueryChange(event.currentTarget.value)}
                placeholder={`Find a song in ${projectTitle}`}
                type="search"
                value={query}
              />
            </label>
            <Button
              className="w-full sm:w-auto"
              onClick={() => onSelectionModeChange(!selectionMode)}
              variant={selectionMode ? 'ghost' : 'secondary'}
            >
              {selectionMode ? <X aria-hidden="true" /> : null}
              {selectionMode ? 'Cancel' : 'Select'}
            </Button>
          </div>
          <DurationRangeFilter onChange={onDurationRangeChange} value={durationRange} />
        </div>
      </section>

      <section aria-label={`Filtered songs from ${projectTitle}`}>
        <TrackCollection
          emptyDescription="Adjust the script result, duration, or title search."
          emptyTitle="No songs match this view"
          errorTitle="Project songs could not be loaded"
          loadAll
          onSelectionModeChange={onSelectionModeChange}
          playbackContext={playbackContext}
          query={trackQuery}
          selectionMode={selectionMode}
          selectionScopeKey={selectionScopeKey}
          transformTracks={transformTracks}
        />
      </section>
    </>
  );
}
